'use client'

import { Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'

import {
  ItemUploadContext,
  useAttributesContext,
} from '@marketplace-web/item-upload/item-upload-data'
import { useSession } from '@marketplace-web/shared/session'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'

import BundleDiscountsModal from './BundleDiscountsModal'

const BundleDiscountsHint = () => {
  const translationKeyId = useMemo(() => uuid(), [])
  const { track } = useTracking()
  const translate = useTranslate('item_upload.price.bundle_discounts_hint')

  const { locale } = useIntl()
  const { user } = useSession()
  const {
    configuration: { currency },
  } = useContext(ItemUploadContext)
  const {
    attributes: { price },
  } = useAttributesContext()

  const [userBundleDiscounts, setUserBundleDiscounts] = useState(user?.bundle_discount)
  const [isDiscountsModalOpen, setIsDiscountsModalOpen] = useState(false)

  const highiestTierDiscount = userBundleDiscounts?.discounts[2]!.fraction

  const handleBundleDiscountsLinkClick = () => {
    track(
      clickEvent({
        target: ClickableElement.BundleDiscountsSettings,
        targetDetails: JSON.stringify({
          enabled: userBundleDiscounts?.enabled,
          discount_up_to: highiestTierDiscount,
          price,
          currency,
        }),
      }),
    )

    setIsDiscountsModalOpen(true)
  }

  const getHintTranslation = () => {
    const highiestTierDiscountNumber = Number(highiestTierDiscount)

    const hintLinkValue = {
      'bundle-discounts-link': (chunks: Array<ReactNode>) => (
        <button key={translationKeyId} onClick={handleBundleDiscountsLinkClick} type="button">
          <Text underline clickable type={Text.Type.Caption} as="span">
            {chunks}
          </Text>
        </button>
      ),
    }

    if (!userBundleDiscounts?.enabled) return translate('off_title', hintLinkValue)

    const discountPercentage = highiestTierDiscountNumber.toLocaleString(locale, {
      style: 'percent',
    })

    return translate('on_title', {
      ...hintLinkValue,
      percentage: discountPercentage,
    })
  }

  const renderContent = () => (
    <>
      <Spacer />
      <Text type={Text.Type.Caption} as="p" testId="bundle-discounts-hint">
        {getHintTranslation()}
      </Text>
    </>
  )

  return (
    <>
      {renderContent()}
      <BundleDiscountsModal
        show={isDiscountsModalOpen}
        onClose={() => setIsDiscountsModalOpen(false)}
        bundleDiscount={userBundleDiscounts}
        onBundleDiscountUpdate={setUserBundleDiscounts}
      />
    </>
  )
}

export default BundleDiscountsHint
